// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getMiniSplitBtus from "@plumberssupply/App/Handlers/SystemBuilder/getMiniSplitBtus";
import getZoneUnitTypes from "@plumberssupply/App/Handlers/SystemBuilder/getZoneUnitTypes";
import { CustomAppState } from "@plumberssupply/App/Store/Reducers";
import { mergeWithCss } from "@plumberssupply/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";

interface OwnProps {
    extendedStyles?: SystemBuilderMiniSplitZoneStyles;
    zoneNumber: number;
}

export interface SystemBuilderMiniSplitZoneStyles {
    select?: SelectPresentationProps;
    title?: TypographyPresentationProps;
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => ({
    btuType: state.xSystemBuilder.miniSplitConfugurations.btuType,
    zoneInfo: state.xSystemBuilder.miniSplitConfugurations.miniSplitZones?.[ownProps.zoneNumber],
    selectedBrand: state.xSystemBuilder.miniSplitConfugurations.selectedBrand,
    miniSplitZones: state.xSystemBuilder.miniSplitConfugurations.miniSplitZones,
    selectedNumberOfZones: state.xSystemBuilder.miniSplitConfugurations?.selectedNumberOfZones,
    miniSplitConfugurations: state.xSystemBuilder.miniSplitConfugurations,
});

const mapDispatchToProps = {
    getZoneUnitTypes,
    getMiniSplitBtus,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export const systemBuilderMiniSplitZoneStyles: SystemBuilderMiniSplitZoneStyles = {};

const SystemBuilderMiniSplitZone: FC<Props> = ({
    extendedStyles,
    zoneNumber,
    zoneInfo,
    btuType,
    getZoneUnitTypes,
    selectedBrand,
    miniSplitZones,
    getMiniSplitBtus,
    selectedNumberOfZones,
    miniSplitConfugurations,
}) => {
    const [selectedBtus, setSelectedBtus] = useState(zoneInfo?.selectedBtus || "");
    const [unitTypes, setUnitTypes] = useState(zoneInfo?.unitTypes || []);
    const [zoneBtus, setZoneBtus] = useState(zoneInfo?.btus || []);
    const [isLoadingBtus, setIsLoadingBtus] = useState(false);
    const [selectedUnitType, setSelectedUnitType] = useState(zoneInfo?.selectedUnitType || "");
    const [styles] = useState(() => mergeWithCss(systemBuilderMiniSplitZoneStyles, extendedStyles));
    const dispatch = useDispatch();

    useEffect(() => {
        if (selectedUnitType !== zoneInfo?.selectedUnitType) {
            setSelectedUnitType(zoneInfo?.selectedUnitType || "");
        }
    }, [zoneInfo?.selectedUnitType]);

    useEffect(() => {
        if (unitTypes !== zoneInfo?.unitTypes) {
            setUnitTypes(zoneInfo?.unitTypes || []);
        }
    }, [zoneInfo?.unitTypes]);

    useEffect(() => {
        if (zoneBtus !== zoneInfo?.btus) {
            setZoneBtus(zoneInfo?.btus || []);
        }
    }, [zoneInfo?.btus]);

    useEffect(() => {
        if (!zoneInfo) {
            setSelectedBtus("");
            setUnitTypes([]);
            setZoneBtus([]);
            setSelectedUnitType("");
        }
    }, [zoneInfo]);

    useEffect(() => {
        if (selectedBtus !== zoneInfo?.selectedBtus) {
            setSelectedBtus(zoneInfo?.selectedBtus || "");
        }
    }, [zoneInfo?.selectedBtus]);

    useEffect(() => {
        let conditionsSatisfied = true;
        const selectedBtusValues = [];
        for (let i = 1; i < zoneNumber; i++) {
            if (
                !miniSplitZones[i]?.btus?.length ||
                !miniSplitZones[i]?.selectedBtus ||
                !miniSplitZones[i]?.selectedUnitType
            ) {
                conditionsSatisfied = false;
                break;
            } else {
                selectedBtusValues.push(miniSplitZones[i].selectedBtus);
            }
        }

        if (conditionsSatisfied && !miniSplitZones[zoneNumber]?.btus?.length && selectedNumberOfZones) {
            // this means all previous zones are validated, so call btus api
            getBtus(selectedNumberOfZones, selectedBrand, btuType, selectedBtusValues);
        }
    }, [selectedNumberOfZones, miniSplitZones]);

    const getBtus = (numberOfZones: string, brand: string, btusType: string, selected: string[]) => {
        if (isLoadingBtus) {
            return;
        }
        setIsLoadingBtus(true);

        getMiniSplitBtus({
            parameter: {
                brand,
                numberOfZones,
                btusType,
                selected,
            },
            onSuccess(result) {
                setIsLoadingBtus(false);
                // sort btus in ascending order
                const btus = result.values?.sort((a: string, b: string) => Number(a) - Number(b));
                setZoneBtus(btus);
                dispatch({
                    type: "xSystemBuilder/UpdateMiniSplitZoneInfo",
                    zoneNumber,
                    zoneInfo: {
                        ...zoneInfo,
                        btus,
                    },
                } as any);
            },
        });
    };

    const btusChangeHandler: React.ChangeEventHandler<HTMLSelectElement> = event => {
        const value = event.currentTarget.value;
        setSelectedBtus(value);
        setSelectedUnitType("");

        if (!value) {
            dispatch({
                type: "xSystemBuilder/UpdateMiniSplitZoneInfo",
                zoneNumber,
                zoneInfo: { ...zoneInfo, selectedBtus: "" },
            } as any);
            return;
        }

        // Disable all other drop downs for next zones when btus changes
        const updatedZones = structuredClone(miniSplitZones);
        updatedZones[zoneNumber] = {
            ...updatedZones[zoneNumber],
            selectedBtus: value,
            unitTypes: [],
            selectedUnitType: "",
        };
        for (let i = zoneNumber + 1; i <= Number(selectedNumberOfZones); i++) {
            updatedZones[i] = { selectedBtus: "", btus: [], unitTypes: [], selectedUnitType: "" };
        }

        dispatch({
            type: "xSystemBuilder/UpdateMiniSplitConfigurationObject",
            miniSplitConfugurations: { ...miniSplitConfugurations, miniSplitZones: updatedZones },
        } as any);

        getZoneUnitTypes({
            parameter: {
                btus: value,
                btusType: btuType,
                brand: selectedBrand,
                numberOfZones: selectedNumberOfZones,
            },
            onSuccess(result) {
                setUnitTypes(result.values);
                let zone = structuredClone(zoneInfo);
                if (!zone) {
                    zone = {
                        unitTypes: result.values,
                        selectedBtus,
                        selectedUnitType: "",
                        btus: zoneBtus,
                    };
                }
                zone.unitTypes = result.values;
                zone.selectedBtus = value;
                zone.selectedUnitType = "";
                dispatch({
                    type: "xSystemBuilder/UpdateMiniSplitZoneInfo",
                    zoneNumber,
                    zoneInfo: zone,
                } as any);
            },
        });
    };

    const unitTypeChangeHandler: React.ChangeEventHandler<HTMLSelectElement> = event => {
        const value = event.currentTarget.value;
        setSelectedUnitType(value);
        // save value in zoneInfo
        let zone = structuredClone(zoneInfo);
        if (!zone) {
            zone = {
                unitTypes,
                selectedBtus,
                selectedUnitType,
                btus: zoneBtus,
            };
        }
        zone.selectedUnitType = value;
        dispatch({
            type: "xSystemBuilder/UpdateMiniSplitZoneInfo",
            zoneNumber,
            zoneInfo: zone,
        } as any);
    };

    return (
        <StyledWrapper>
            <Typography {...styles.title}>{`${translate("Zone")} ${zoneNumber}`}</Typography>
            <Select
                disabled={!zoneBtus.length}
                {...styles.select}
                value={selectedBtus || ""}
                onChange={btusChangeHandler}
            >
                <option value="">{translate("Select BTUs")}</option>
                {zoneBtus?.map(btu => (
                    <option key={btu} value={btu}>
                        {btu}
                    </option>
                ))}
            </Select>

            <Select
                disabled={!selectedBtus || !unitTypes.length}
                value={selectedUnitType || ""}
                onChange={unitTypeChangeHandler}
            >
                <option value="">{translate("Select Unit Type")}</option>
                {unitTypes?.map(unitType => (
                    <option key={unitType} value={unitType}>
                        {unitType}
                    </option>
                ))}
            </Select>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SystemBuilderMiniSplitZone);
